/**
 * ChatFABPanel — Unified floating chat FAB with Sage + Team Chat tabs
 *
 * Always visible. FAB sits bottom-right.
 * Click → opens a compact floating panel (360×480px).
 * Expand icon → side panel (400px full-height slide-over).
 *
 * Sage tab: embeds the inline SageEmbed component below (no own panel chrome).
 * Team Chat tab: embeds the inline TeamChatEmbed component below, backed by the
 *   shared useTeamChat hook (SSE-live, same data layer as the /chat page).
 *
 * Features:
 *  1. @mention dropdown
 *  2. Reaction bubbles on messages
 *  3. File/attachment support
 *  4. Hover action bar (reply, react, pin)
 *  5. Formatting toolbar
 */
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import { useLocation, useParams } from 'react-router-dom'
import { MessageSquare, X, Maximize2, Minimize2, Hash, AtSign, Send, PenSquare, History, ChevronLeft, Trash2, Plus, Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon, Strikethrough, Link, ListOrdered, List, Quote, Smile, Video, Pin, CornerUpLeft, Forward, Edit2 } from 'lucide-react'
import { tokens } from '@/design-system/tokens'
import { sanitizeHtml } from '@/lib/sanitize'
import { useBranding } from '@/contexts/BrandingContext'
import { useSSEEvent } from '@/hooks/useSSE'
import { SageConversationMode } from '@/components/organisms/SageConversationMode'
import { SageComposer, type SageComposerHandle } from '@/components/molecules/SageComposer'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'
import { AttachmentViewer, type ViewerAttachment } from '@/components/task/shared/AttachmentViewer'
import { attachmentUrl } from '@/components/task/shared/attachments'
import { formatDate, formatTimeWithOptions } from '@/utils/dateUtils'
import { authedFetch } from '@/lib/api'
import { readSageStream } from '@/lib/sageStream'
import { useTeamChat, type ChatChannel, type ChatMessage, type ChatAttachment, type TeamUser } from '@/hooks/useTeamChat'
import { useNetworkStatus } from '@/lib/native'
import { enqueue, drain, listPending, newClientToken, type OutboxOp } from '@/lib/offlineOutbox'

/** Width of the expanded chat panel, and of the space the app shell yields to
 *  it while docked. Single source of truth: the panel's own width AND the
 *  `--chat-dock-w` custom property SidebarLayout subtracts from the shell. */
const DOCK_WIDTH = 400

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'sage' | 'team' | 'drafts'
type PanelMode = 'compact' | 'expanded'

// ── TeamChat embed (light theme) ──────────────────────────────────────────────
// ChatChannel / ChatMessage / ChatAttachment / TeamUser are the canonical types
// owned by useTeamChat (the shared data layer) — imported above, no longer
// redeclared here, so the FAB panel and /chat page can never drift. See
// docs/features/teamchat-web-consolidate.md.

interface PendingFile { file: File; preview: string; uploading: boolean; uploadedUrl?: string; uploadedMime?: string; error?: boolean }

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso)
    return formatTimeWithOptions(d, { hour: 'numeric', minute: '2-digit' })
  } catch { return '' }
}
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}
// Group order matching Settings → Team Chat's admin channel list (#2114): general/
// company channels first, then project channels, then everything else. Same
// grouping on both surfaces so they read as one system instead of looking unrelated.
function channelGroupRank(type: string): number {
  if (type === 'general' || type === 'watercooler') return 0
  if (type === 'project') return 1
  return 2
}
function sortChannelsForDisplay(list: ChatChannel[]): ChatChannel[] {
  return [...list].sort((a, b) => channelGroupRank(a.type) - channelGroupRank(b.type) || a.display_name.localeCompare(b.display_name))
}
function avatarInitial(name: string): string { return name?.charAt(0)?.toUpperCase() || '?' }
const AVATAR_COLORS = ['var(--st-active)','#0891b2','#059669','var(--brand-gold)','#dc2626','#7c3aed']
function avatarColor(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

// ── Mention Dropdown ──────────────────────────────────────────────────────────
function MentionDropdown({ users, query, onSelect }: { users: TeamUser[]; query: string; onSelect: (u: TeamUser) => void }) {
  const filtered = users.filter(u => u.name.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
  if (filtered.length === 0) return null
  return (
    <div style={{
      position: 'absolute', bottom: '100%', left: 0, right: 0,
      background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8,
      boxShadow: '0 -4px 16px rgba(0,0,0,0.12)', zIndex: 30,
      maxHeight: 180, overflowY: 'auto', marginBottom: 4,
    }}>
      {filtered.map(u => (
        <button
          key={u.id}
          onMouseDown={e => { e.preventDefault(); onSelect(u) }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            width: '100%', padding: '7px 10px', background: 'none',
            border: 'none', cursor: 'pointer', textAlign: 'left',
            fontSize: 13, color: tokens.color.text.body,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          <div style={{
            width: 24, height: 24, borderRadius: '50%',
            background: avatarColor(u.name), color: 'var(--bg-page)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, flexShrink: 0,
          }}>{avatarInitial(u.name)}</div>
          <span style={{ fontWeight: 500 }}>{u.name}</span>
          {u.email && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{u.email}</span>}
        </button>
      ))}
    </div>
  )
}

function TeamChatEmbed({ mobileFullScreen = false, onRead }: { mobileFullScreen?: boolean; onRead?: () => void }) {
  const endRef = useRef<HTMLDivElement>(null)

  // Shared data layer — the same useTeamChat hook the full /chat page uses. This
  // panel now receives live SSE updates (message moves, reactions, pins from
  // other users) instead of only catching them on the next 5s poll tick, and no
  // longer carries its own drifted copy of the channel/message/send/react/pin
  // logic (its old reaction endpoint even 404'd). redirectOnAuthFailure is false
  // so a transient chat 401 never yanks the user out of whatever page this global
  // FAB is floating over. See docs/features/teamchat-web-consolidate.md.
  const {
    channels,
    activeChannel: active, selectChannel,
    messages,
    teamUsers,
    myUserId: currentUserId, myRole: currentUserRole,
    sendMessage: sendChannelMessage,
    saveEdit: saveChannelEdit,
    deleteMessage: deleteChannelMessage,
    togglePin,
    toggleReact,
    moveMessage: moveChannelMessage,
    markUnread: markUnreadChannel,
    openDM,
  } = useTeamChat({
    initialChannel: 'general',
    redirectOnAuthFailure: false,
    onRead,
    onScrollToBottom: (behavior) => { endRef.current?.scrollIntoView({ behavior }) },
  })

  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [showChannelList, setShowChannelList] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [showDMPicker, setShowDMPicker] = useState(false)
  const [editingMsg, setEditingMsg] = useState<ChatMessage | null>(null)
  const [threadParent, setThreadParent] = useState<ChatMessage | null>(null)
  const [threadMessages, setThreadMessages] = useState<ChatMessage[]>([])
  const [showThread, setShowThread] = useState(false)
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState<number | null>(null)
  const [movingMsgId, setMovingMsgId] = useState<number | null>(null)
  // @mention
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  // file attachments — rich thumbnails
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  // Offline-resilient send (#2488, mirrors TeamChatPage's #2463 wiring): a
  // genuine dropped-connection failure on Send durably queues {content, files}
  // instead of losing the draft. Scoped per channel (`chat-message:${channelId}`)
  // — the same kind TeamChatPage uses, so either surface can drain a queue the
  // other one enqueued for a shared channel.
  const [pendingSends, setPendingSends] = useState<{ clientToken: string; content: string; fileCount: number }[]>([])
  const net = useNetworkStatus()
  // formatting toolbar visibility
  const [showFormatBar, setShowFormatBar] = useState(true)
  // hover actions
  const [hoveredMsgId, setHoveredMsgId] = useState<number | null>(null)
  // context menu for mark-as-unread
  const [contextMenu, setContextMenu] = useState<{ channelId: number; x: number; y: number } | null>(null)
  // image/attachment preview — owns the AttachmentViewer. Fixes #2018 where
  // MessageRow called a setPreviewAttachment that only existed in SageEmbed's
  // scope (sibling component), throwing ReferenceError on image click.
  const [previewAttachment, setPreviewAttachment] = useState<ViewerAttachment | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [teamKeyboardOffset, setTeamKeyboardOffset] = useState(0)

  // Switch channels: the hook clears the message list + loads the new one; we
  // also drop any open thread view (local UI) and close the mobile sidebar.
  const switchChannel = useCallback((ch: ChatChannel) => {
    selectChannel(ch)
    setShowThread(false)
    setThreadParent(null)
    setShowSidebar(false)
    setShowChannelList(false)
  }, [selectChannel])

  const markChannelUnread = async (channelId: number) => {
    await markUnreadChannel(channelId)
    setContextMenu(null)
  }

  useEffect(() => {
    const kbHandler = () => {
      if (!window.visualViewport) return
      const kbH = Math.max(0, window.innerHeight - window.visualViewport.height - (window.visualViewport.offsetTop || 0))
      setTeamKeyboardOffset(kbH)
    }
    window.visualViewport?.addEventListener('resize', kbHandler)
    window.visualViewport?.addEventListener('scroll', kbHandler)
    return () => {
      window.visualViewport?.removeEventListener('resize', kbHandler)
      window.visualViewport?.removeEventListener('scroll', kbHandler)
    }
  }, [mobileFullScreen])

  const QUICK_EMOJIS = ['👍','👎','❤️','😂','🎉','🙏']

  // NOTE: channel list, team users, active-channel message load, the 5s poll
  // backstop, live SSE subscriptions, and mark-as-read on channel open are ALL
  // owned by useTeamChat now (see the destructure above) — the FAB panel no
  // longer runs its own drifted copies. onRead is threaded into the hook so
  // SidebarLayout's external unread badge still refreshes on channel open.

  // Derive MIME type from filename extension as a fallback
  const mimeFromExt = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() ?? ''
    const map: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
      webp: 'image/webp', heic: 'image/heic', heif: 'image/heic',
      mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo',
      mkv: 'video/x-matroska', webm: 'video/webm',
      pdf: 'application/pdf',
    }
    return map[ext] ?? 'application/octet-stream'
  }

  // Upload a single file immediately on select — returns { url, mime_type } from server
  const uploadSingleFile = async (pf: PendingFile, channelId: number): Promise<{ url: string; mime_type: string } | undefined> => {
    const form = new FormData()
    form.append('file', pf.file)
    try {
      const res = await authedFetch(`/api/v1/chat/channels/${channelId}/attachments`, {
        method: 'POST',
        body: form,
      }, { redirectOnAuthFailure: false })
      if (!res.ok) throw new Error('Upload failed')
      const data = await res.json()
      const att = data.attachments?.[0]
      if (!att?.url) return undefined
      return { url: att.url, mime_type: att.mime_type || '' }
    } catch {
      return undefined
    }
  }

  // Rehydrate this channel's queued-but-unsent messages on mount / channel
  // switch, so reopening the panel while offline still shows what hasn't synced.
  useEffect(() => {
    if (!active) { setPendingSends([]); return }
    const kind = `chat-message:${active.id}`
    listPending(kind).then(ops => {
      setPendingSends(ops.map(o => ({
        clientToken: o.clientToken,
        content: (o.payload.content as string) || '',
        fileCount: o.blobs?.length || 0,
      })))
    }).catch(() => {})
  }, [active?.id])

  // Upload every queued blob for one drained op in a single multipart request —
  // mirrors TeamChatPage's uploadFiles, targeting the same attachments endpoint.
  const uploadQueuedFiles = async (blobs: { blob: Blob; fileName: string }[], channelId: number): Promise<ChatAttachment[]> => {
    const form = new FormData()
    blobs.forEach(b => form.append('file', b.blob, b.fileName))
    const res = await authedFetch(`/api/v1/chat/channels/${channelId}/attachments`, {
      method: 'POST', body: form,
    }, { redirectOnAuthFailure: false })
    if (!res.ok) throw new Error('Upload failed')
    const data = await res.json()
    return data.attachments || []
  }

  // Drain the active channel's queue: re-upload any files, then replay through
  // the same sendChannelMessage() the interactive Send button uses. The
  // clientToken makes the replay idempotent server-side, so a queued send whose
  // original request actually succeeded (response just never reached the
  // device) cannot create a duplicate message.
  const drainChatQueue = useCallback(async () => {
    const ch = active
    if (!ch) return
    const kind = `chat-message:${ch.id}`
    const before = await listPending(kind)
    if (before.length === 0) return
    await drain(kind, async (op: OutboxOp): Promise<'ok' | 'retry'> => {
      try {
        let attachments: ChatAttachment[] = []
        if (op.blobs && op.blobs.length > 0) {
          attachments = await uploadQueuedFiles(op.blobs, ch.id)
        }
        await sendChannelMessage({ content: (op.payload.content as string) || '', attachments, clientToken: op.clientToken })
        return 'ok'
      } catch {
        return 'retry'
      }
    })
    const after = await listPending(kind)
    setPendingSends(after.map(o => ({
      clientToken: o.clientToken,
      content: (o.payload.content as string) || '',
      fileCount: o.blobs?.length || 0,
    })))
  }, [active, sendChannelMessage])

  useEffect(() => {
    if (net.connected && active) drainChatQueue().catch(() => {})
  }, [net.connected, active?.id, drainChatQueue])

  const handleFileSelect = async (files: File[]) => {
    if (!active) return
    const available = 5 - pendingFiles.length
    const toAdd = files.slice(0, available)
    if (toAdd.length === 0) return
    const newEntries: PendingFile[] = toAdd.map(f => ({
      file: f,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : '',
      uploading: true,
    }))
    setPendingFiles(prev => [...prev, ...newEntries])
    // upload each immediately
    const channelId = active.id
    for (const entry of newEntries) {
      const result = await uploadSingleFile(entry, channelId)
      setPendingFiles(prev => prev.map(p =>
        p.file === entry.file
          ? { ...p, uploading: false, uploadedUrl: result?.url, uploadedMime: result?.mime_type, error: !result?.url }
          : p
      ))
    }
  }

  const handleSaveEdit = async () => {
    if (!editingMsg) return
    const content = input.trim()
    if (!content) return
    const updated = await saveChannelEdit(editingMsg, content)
    if (updated) {
      setEditingMsg(null)
      setInput('')
    }
  }

  const handleSend = async (isThread = false) => {
    // If in edit mode, delegate to save edit
    if (editingMsg && !isThread) { await handleSaveEdit(); return }
    const content = input.trim()
    const readyFiles = pendingFiles.filter(p => !p.uploading && p.uploadedUrl)
    if (!content && readyFiles.length === 0) return
    if (sending) return
    const targetChannel = active
    if (!targetChannel) return
    setSending(true)
    const attachments: ChatAttachment[] = readyFiles
      .filter(p => p.uploadedUrl)
      .map(p => ({
        url: p.uploadedUrl!,
        filename: p.file.name,
        size: p.file.size,
        // Prefer server-returned MIME, then browser-reported, then derive from extension
        mime_type: p.uploadedMime || p.file.type || mimeFromExt(p.file.name),
      }))
    // Raw files behind this send, captured before the composer clears — used
    // only if we fall back to the offline outbox below. Every pendingFile
    // (not just ones that finished uploading) so a file whose eager upload
    // itself failed offline still gets queued instead of silently dropped.
    const filesToQueue = pendingFiles.map(p => p.file)
    // Clear the composer up-front (optimistic); restore the text on failure.
    setInput('')
    setMentionQuery(null)
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    // revoke object URLs to avoid memory leaks
    pendingFiles.forEach(p => { if (p.preview) URL.revokeObjectURL(p.preview) })
    setPendingFiles([])
    try {
      if (isThread && threadParent) {
        // Thread replies stay panel-local — useTeamChat owns the channel message
        // stream, not thread panels (they diverge between the two surfaces).
        const tempId = -(Date.now())
        const optimistic: ChatMessage = {
          id: tempId, channel_id: targetChannel.id, user_id: currentUserId, user_name: 'You',
          content: content || ' ', created_at: new Date().toISOString(), edited_at: null, reactions: [],
        }
        setThreadMessages(prev => [...prev, optimistic])
        const body: Record<string, unknown> = { content: content || ' ', parent_message_id: threadParent.id }
        if (attachments.length > 0) body.attachments = attachments
        const res = await authedFetch(`/api/v1/chat/channels/${targetChannel.id}/messages`, {
          method: 'POST', body: JSON.stringify(body)
        }, { redirectOnAuthFailure: false })
        if (!res.ok) throw new Error('Send failed')
        const real = await res.json()
        setThreadMessages(prev => prev.some(m => m.id === real.id)
          ? prev.filter(m => m.id !== tempId)
          : prev.map(m => m.id === tempId ? real : m))
      } else {
        // Channel send goes through the shared hook — identical optimistic-then-
        // reconcile-by-id path the /chat page uses, writing into the shared stream.
        await sendChannelMessage({ content: content || ' ', attachments })
      }
    } catch (e) {
      // Same distinction #2463 uses: a genuine dropped connection (fetch()
      // throwing a TypeError, not a real HTTP rejection like a 4xx/5xx)
      // durably queues the send instead of losing the draft. Thread replies
      // aren't wired onto the outbox — panel-local, out of scope here.
      if (!isThread && e instanceof TypeError && targetChannel) {
        const clientToken = newClientToken()
        try {
          await enqueue({
            kind: `chat-message:${targetChannel.id}`,
            clientToken,
            capturedAt: new Date().toISOString(),
            payload: { content, channelId: targetChannel.id },
            blobs: filesToQueue.length ? filesToQueue.map(f => ({ blob: f, fileName: f.name })) : undefined,
          })
          setPendingSends(prev => [...prev, { clientToken, content, fileCount: filesToQueue.length }])
          return
        } catch (queueErr) {
          console.error('ChatFABPanel: failed to queue offline send', queueErr)
        }
      }
      console.error('ChatFABPanel: failed to send message', e)
      setInput(content)
    } finally {
      setSending(false)
    }
  }

  // Reactions + pins delegate to the shared hook, which owns the optimistic
  // update against the shared message stream and the correct endpoints (the
  // FAB's old copy hit a reaction path that 404'd and refetched the whole
  // channel to recover). Take the full message so the hook can toggle by object.
  const handleReact = (msg: ChatMessage, emoji: string) => {
    setReactionPickerMsgId(null)
    toggleReact(msg, emoji)
  }

  const handlePin = (msg: ChatMessage) => {
    togglePin(msg)
  }

  const loadThreadMessages = async (msg: ChatMessage) => {
    if (!active) return
    setThreadParent(msg)
    setShowThread(true)
    try {
      const res = await authedFetch(
        `/api/v1/chat/channels/${active.id}/messages/${msg.id}/thread`,
        {},
        { redirectOnAuthFailure: false }
      )
      if (!res.ok) throw new Error('Thread load failed')
      const data = await res.json()
      setThreadMessages(data?.messages || [])
    } catch {
      setThreadMessages([])
    }
  }

  // Formatting toolbar helper
  const applyFormat = (prefix: string, suffix: string) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = input.slice(start, end)
    const newVal = input.slice(0, start) + prefix + (selected || 'text') + suffix + input.slice(end)
    setInput(newVal)
    setTimeout(() => {
      ta.focus()
      ta.setSelectionRange(start + prefix.length, start + prefix.length + (selected || 'text').length)
    }, 0)
  }

  // Simple markdown: bold, italic, code, links, @mentions
  function renderMarkdown(text: string): string {
    // Split on @[Name:id] tokens, process each part separately
    const parts = text.split(/(@\[[^\]]+:\d+\])/g)
    const raw = parts.map(part => {
      const mentionMatch = part.match(/^@\[([^\]]+):(\d+)\]$/)
      if (mentionMatch) {
        const name = mentionMatch[1].replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        return `<span style="background:#eef2ff;color:#4338ca;font-weight:600;border-radius:4px;padding:1px 5px">@${name}</span>`
      }
      return part
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-family:monospace;font-size:12px">$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/~~([^~]+)~~/g, '<s>$1</s>')
        .replace(/__([^_]+)__/g, '<u>$1</u>')
        .replace(/^> (.+)/gm, '<blockquote style="border-left:3px solid #e2e8f0;margin:2px 0;padding-left:8px;color:#64748b">$1</blockquote>')
        .replace(/^- (.+)/gm, '• $1')
        .replace(/^\d+\. (.+)/gm, (m, item, offset, str) => {
          const num = str.slice(0, offset).match(/^\d+\./gm)?.length || 1
          return `${num}. $1`
        })
        .replace(/\n/g, '<br>')
    }).join('')
    return sanitizeHtml(raw)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, isThread = false) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(isThread) }
    if (e.key === 'Escape') setMentionQuery(null)
  }

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setInput(val)
    const ta = textareaRef.current
    if (ta) { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 120) + 'px' }
    // @mention detection
    const cursor = e.target.selectionStart
    const textBefore = val.slice(0, cursor)
    const mentionMatch = textBefore.match(/@(\w*)$/)
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1])
    } else {
      setMentionQuery(null)
    }
  }

  const handleMentionSelect = (user: TeamUser) => {
    if (mentionQuery === null) return
    const atPos = input.lastIndexOf('@')
    if (atPos === -1) return
    const before = input.slice(0, atPos)
    const after = input.slice(atPos + 1 + mentionQuery.length)
    setInput(before + `@[${user.name}:${user.id}]` + after + ' ')
    setMentionQuery(null)
    textareaRef.current?.focus()
  }

  const channelIcon = active?.type === 'dm'
    ? <AtSign size={13} style={{ color: 'var(--text-secondary)' }} />
    : <Hash size={13} style={{ color: 'var(--text-secondary)' }} />

  function MessageRow({ msg, inThread = false }: { msg: ChatMessage; inThread?: boolean }) {
    const replyCount = msg.reply_count || 0
    const isShowingPicker = reactionPickerMsgId === msg.id
    const isHovered = hoveredMsgId === msg.id
    return (
      <div
        style={{ display: 'flex', gap: 8, padding: '4px 12px', opacity: msg.id < 0 ? 0.6 : 1, position: 'relative' }}
        onMouseEnter={() => setHoveredMsgId(msg.id)}
        onMouseLeave={() => { setHoveredMsgId(null); if (isShowingPicker) setReactionPickerMsgId(null) }}
      >
        {/* Hover action bar — top right */}
        {isHovered && msg.id > 0 && (
          <div style={{
            position: 'absolute', top: 0, right: 12,
            display: 'flex', gap: 2, background: 'var(--bg-elevated)',
            border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: '2px 4px', zIndex: 10,
          }}>
            {/* Reply */}
            <button
              onClick={() => loadThreadMessages(msg)}
              title="Reply in thread"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px 5px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 3 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              <CornerUpLeft size={14} />
            </button>
            {/* React */}
            <button
              onClick={() => setReactionPickerMsgId(isShowingPicker ? null : msg.id)}
              title="Add reaction"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px 5px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              <Smile size={14} />
            </button>
            {/* Pin */}
            {!inThread && (
              <button
                onClick={() => handlePin(msg)}
                title={msg.is_pinned ? 'Unpin message' : 'Pin message'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: msg.is_pinned ? 'var(--brand-gold)' : 'var(--text-secondary)', padding: '2px 5px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <Pin size={14} style={msg.is_pinned ? { fill: 'var(--brand-gold)' } : {}} />
              </button>
            )}
            {/* Edit — own messages only */}
            {msg.user_id === currentUserId && (
              <button
                onClick={() => { setEditingMsg(msg); setInput(msg.content) }}
                title="Edit message"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px 5px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <Edit2 size={14} />
              </button>
            )}
            {/* Delete — own or admin */}
            {(msg.user_id === currentUserId || currentUserRole === 'admin' || currentUserRole === 'super_admin') && (
              <button
                onClick={() => {
                  if (!window.confirm('Delete this message?')) return
                  deleteChannelMessage(msg)
                }}
                title={msg.user_id !== currentUserId ? 'Delete (admin)' : 'Delete message'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--st-error)', padding: '2px 5px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'color-mix(in srgb, var(--st-error) 8%, transparent)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <Trash2 size={14} />
              </button>
            )}
            {/* Move to channel — admin only */}
            {!inThread && (currentUserRole === 'admin' || currentUserRole === 'super_admin') && channels.filter(c => c.id !== active?.id && c.type !== 'dm').length > 0 && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMovingMsgId(movingMsgId === msg.id ? null : msg.id)}
                  title="Move to channel"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px 5px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <Forward size={14} />
                </button>
                {movingMsgId === msg.id && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 2, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 160, maxHeight: 200, overflowY: 'auto', padding: '4px 0' }}>
                    <div style={{ padding: '4px 10px 2px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.06em' }}>Move to</div>
                    {channels.filter(c => c.id !== active?.id && c.type !== 'dm').map(ch => (
                      <button key={ch.id}
                        onClick={() => {
                          setMovingMsgId(null)
                          moveChannelMessage(msg, ch.id)
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%', padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 12, color: tokens.color.text.body }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                      >
                        # {ch.display_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Emoji picker popup */}
            {isShowingPicker && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4,
                display: 'flex', gap: 4, background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '4px 6px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                zIndex: 20,
              }}>
                {QUICK_EMOJIS.map(em => (
                  <button key={em} onClick={() => handleReact(msg, em)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '1px 2px' }}>
                    {em}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Avatar */}
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: avatarColor(msg.user_name),
          color: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}>
          {avatarInitial(msg.user_name)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 1 }}>
            <span style={{ fontWeight: 600, fontSize: 12, color: tokens.color.text.body }}>{msg.user_name}</span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{fmtTime(msg.created_at)}</span>
            {msg.edited_at && <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>(edited)</span>}
            {msg.is_pinned && <Pin size={10} style={{ color: 'var(--brand-gold)', fill: 'var(--brand-gold)' }} />}
          </div>

          {/* Message content */}
          <div
            style={{ fontSize: 13, color: tokens.color.text.body, lineHeight: 1.5, wordBreak: 'break-word', overflowWrap: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
          />

          {/* Attachments */}
          {msg.attachments && msg.attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {msg.attachments.map((att, i) => (
                att.mime_type?.startsWith('image/') ? (
                  <img
                    key={i}
                    src={attachmentUrl(att)}
                    alt={att.filename}
                    style={{ maxWidth: 160, maxHeight: 120, borderRadius: 6, cursor: 'pointer', objectFit: 'cover', border: '1px solid #e2e8f0' }}
                    onClick={() => setPreviewAttachment({ url: attachmentUrl(att), filename: att.filename, mimeType: att.mime_type, size: att.size })}
                  />
                ) : att.mime_type?.startsWith('video/') ? (
                  <video
                    key={i}
                    src={attachmentUrl(att)}
                    controls
                    style={{ maxWidth: 260, maxHeight: 180, borderRadius: 6, border: '1px solid #e2e8f0', background: 'var(--bg-page)', display: 'block' }}
                  />
                ) : att.mime_type === 'application/pdf' ? (
                  <a key={i} href={attachmentUrl(att)} target="_blank" rel="noopener"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 5, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: 'var(--st-error)', textDecoration: 'none', fontSize: 11 }}>
                    📄 {att.filename} · {formatSize(att.size)}
                  </a>
                ) : (
                  <a key={i} href={attachmentUrl(att)} target="_blank" rel="noopener"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 5, background: 'rgba(0,0,0,0.05)', border: '1px solid var(--border)', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 11 }}>
                    📎 {att.filename} · {formatSize(att.size)}
                  </a>
                )
              ))}
            </div>
          )}

          {/* Reaction bubbles */}
          {msg.reactions && msg.reactions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {msg.reactions.map((r, i) => (
                <button
                  key={i}
                  onClick={() => handleReact(msg, r.emoji)}
                  style={{
                    background: currentUserId && r.user_ids.includes(currentUserId) ? 'color-mix(in srgb, var(--st-active) 13%, transparent)' : 'var(--bg-elevated)',
                    border: currentUserId && r.user_ids.includes(currentUserId) ? '1px solid #818cf8' : '1px solid #e2e8f0',
                    borderRadius: 99, padding: '2px 7px', cursor: 'pointer',
                    fontSize: 12, display: 'flex', alignItems: 'center', gap: 3,
                  }}
                >
                  {r.emoji} <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{r.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Thread reply count (no standalone Reply button — use hover bar) */}
          {!inThread && replyCount > 0 && (
            <button
              onClick={() => loadThreadMessages(msg)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--st-active)', padding: '2px 0', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <MessageSquare size={12} /> {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: tokens.color.surface.surface }}>
      {/* Channel header */}
      {showThread && threadParent ? (
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6, background: tokens.color.surface.surface, flexShrink: 0 }}>
          <button onClick={() => setShowThread(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-secondary)', display: 'flex' }}>
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontWeight: 600, fontSize: 13, color: tokens.color.text.body }}>Thread</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {threadParent.content.slice(0, 40)}
          </span>
        </div>
      ) : (
        <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6, background: tokens.color.surface.surface, flexShrink: 0 }}>
          <button
            onClick={() => setShowSidebar(s => !s)}
            title="Channel list"
            style={{ background: showSidebar ? 'var(--bg-elevated)' : 'none', border: 'none', cursor: 'pointer', padding: '3px 6px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)', flex: 1, minWidth: 0 }}
          >
            <Hash size={14} style={{ flexShrink: 0 }} />
            <span style={{ fontWeight: 600, fontSize: 13, color: tokens.color.text.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {active ? active.display_name : 'Loading...'}
            </span>
          </button>
          <button
            onClick={() => setShowDMPicker(true)}
            title="New direct message"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 5px', borderRadius: 4, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}
          >
            <AtSign size={13} />
          </button>
          {channels.reduce((s, c) => s + (c.unread_count || 0), 0) > 0 && (
            <span style={{ background: 'var(--st-error)', color: 'var(--bg-page)', borderRadius: 99, fontSize: 10, fontWeight: 700, padding: '1px 5px', flexShrink: 0 }}>
              {channels.reduce((s, c) => s + (c.unread_count || 0), 0)}
            </span>
          )}
        </div>
      )}

      {/* ── Sidebar nav (slide-in over messages) ── */}
      {showSidebar && !showThread && (
        <div style={{
          position: 'absolute', top: 44, left: 0, bottom: 0, width: '100%',
          background: tokens.color.surface.surface, zIndex: 15, display: 'flex', flexDirection: 'column',
          borderTop: '1px solid var(--border)', overflow: 'hidden',
        }}>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* Channels section */}
            <div style={{ padding: '10px 12px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)' }}>Channels</div>
            {sortChannelsForDisplay(channels.filter(c => c.type !== 'dm')).map(ch => (
              <button key={ch.id}
                onClick={() => switchChannel(ch)}
                onContextMenu={e => { e.preventDefault(); setContextMenu({ channelId: ch.id, x: e.clientX, y: e.clientY }) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                  padding: '7px 12px', background: active?.id === ch.id ? 'var(--bg-elevated)' : 'none',
                  border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 4,
                  color: active?.id === ch.id ? tokens.color.text.body : 'var(--text-secondary)', fontSize: 13,
                  fontWeight: active?.id === ch.id ? 600 : 400,
                }}>
                <Hash size={13} style={{ opacity: 0.6, flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.display_name}</span>
                {ch.unread_count > 0 && <span style={{ background: 'var(--st-error)', color: 'var(--bg-page)', borderRadius: 99, fontSize: 10, fontWeight: 700, padding: '1px 5px', flexShrink: 0 }}>{ch.unread_count}</span>}
              </button>
            ))}
            {/* DMs section */}
            <div style={{ padding: '10px 12px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Direct Messages</span>
              <button onClick={() => { setShowDMPicker(true); setShowSidebar(false) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex', alignItems: 'center' }}><Plus size={13} /></button>
            </div>
            {channels.filter(c => c.type === 'dm').length === 0 && (
              <div style={{ padding: '4px 12px 8px', fontSize: 12, color: 'var(--text-muted)' }}>No DMs yet — click + to start one</div>
            )}
            {channels.filter(c => c.type === 'dm').map(ch => (
              <button key={ch.id}
                onClick={() => switchChannel(ch)}
                onContextMenu={e => { e.preventDefault(); setContextMenu({ channelId: ch.id, x: e.clientX, y: e.clientY }) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                  padding: '7px 12px', background: active?.id === ch.id ? 'var(--bg-elevated)' : 'none',
                  border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 4,
                  color: active?.id === ch.id ? tokens.color.text.body : 'var(--text-secondary)', fontSize: 13,
                  fontWeight: active?.id === ch.id ? 600 : 400,
                }}>
                <AtSign size={13} style={{ opacity: 0.6, flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.display_name}</span>
                {ch.unread_count > 0 && <span style={{ background: 'var(--st-error)', color: 'var(--bg-page)', borderRadius: 99, fontSize: 10, fontWeight: 700, padding: '1px 5px', flexShrink: 0 }}>{ch.unread_count}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Channel context menu ── */}
      {contextMenu && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 100 }}
            onClick={() => setContextMenu(null)}
            onContextMenu={e => { e.preventDefault(); setContextMenu(null) }}
          />
          <div style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 101,
            background: 'var(--bg-elevated, #fff)',
            border: '1px solid var(--border, #e2e8f0)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            minWidth: 160,
            overflow: 'hidden',
          }}>
            <button
              onClick={() => markChannelUnread(contextMenu.channelId)}
              style={{
                display: 'block', width: '100%', padding: '9px 14px',
                background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', fontSize: 13, color: tokens.color.text.body,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              Mark as unread
            </button>
          </div>
        </>
      )}

      {/* ── DM Picker overlay ── */}
      {showDMPicker && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowDMPicker(false)}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 10, width: 280, maxHeight: 360, display: 'flex', flexDirection: 'column', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 13, color: tokens.color.text.body, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              New Direct Message
              <button onClick={() => setShowDMPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
              {teamUsers.filter(u => u.id !== currentUserId).map(u => (
                <button key={u.id}
                  onClick={async () => {
                    setShowDMPicker(false)
                    const ch = await openDM(u)
                    if (ch) setShowSidebar(false)
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: avatarColor(u.name), color: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{avatarInitial(u.name)}</div>
                  <span style={{ fontSize: 13, color: tokens.color.text.body }}>{u.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', minHeight: 0 }}>
        {!active && <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>Loading channels...</div>}
        {showThread && threadParent ? (
          <>
            <div style={{ background: 'var(--bg-elevated)', margin: '0 8px 8px', borderRadius: 8, padding: '4px 0' }}>
              <MessageRow msg={threadParent} inThread />
            </div>
            <div style={{ padding: '4px 12px 4px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{threadMessages.length} {threadMessages.length === 1 ? 'reply' : 'replies'}</div>
            {threadMessages.map(msg => <MessageRow key={msg.id} msg={msg} inThread />)}
          </>
        ) : (
          <>
            {active && messages.length === 0 && <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>No messages yet. Be the first!</div>}
            {messages.map(msg => <MessageRow key={msg.id} msg={msg} />)}
          </>
        )}
        <div ref={endRef} />
      </div>

      {/* ── Slack-style 3-row composer ── */}
      {pendingSends.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          margin: '6px 12px 0', padding: '3px 8px',
          borderRadius: 6, width: 'fit-content',
          background: 'var(--brand-gold)', color: 'var(--brand-gold-text)',
          fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}>
          {pendingSends.length} to send · will sync
        </div>
      )}
      {editingMsg && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', background: 'color-mix(in srgb, var(--st-warning) 10%, transparent)', fontSize: 11, color: 'var(--st-warning)', flexShrink: 0, borderTop: '1px solid #fde68a' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Edit2 size={12} /> Editing message</span>
          <button onClick={() => { setEditingMsg(null); setInput('') }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--st-warning)', padding: 0 }}>✕</button>
        </div>
      )}
      <div style={{
        margin: '0 0 0 0',
        borderTop: '1px solid #e2e8f0',
        background: tokens.color.surface.surface,
        flexShrink: 0,
        // mobileFullScreen: ChatFABPanel already lifts above nav + keyboard — no double offset needed
        paddingBottom: teamKeyboardOffset > 0 && !mobileFullScreen
          ? `${teamKeyboardOffset}px`
          : mobileFullScreen
            ? '0px'
            : 'max(0px, env(safe-area-inset-bottom))',
      }}>
        <style>{`
          @keyframes fabSpinner { to { transform: rotate(360deg); } }
          .fab-toolbar-btn { background: transparent; border: none; border-radius: 4px; padding: 3px 5px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #64748b; transition: background 0.1s, color 0.1s; }
          .fab-toolbar-btn:hover { background: #f1f5f9; color: #334155; }
          .fab-action-btn { background: transparent; border: none; border-radius: 6px; padding: 4px 5px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #64748b; transition: background 0.1s, color 0.1s; }
          .fab-action-btn:hover { background: #f1f5f9; color: #334155; }
          .fab-action-btn:disabled { opacity: 0.4; cursor: default; }
          .fab-action-btn:disabled:hover { background: transparent; color: #64748b; }
        `}</style>

        {/* Outer card */}
        <div style={{
          margin: '8px',
          border: '1px solid var(--border)',
          borderRadius: 8,
          overflow: 'visible',
          position: 'relative',
        }}>
          {/* @mention dropdown — anchored above composer */}
          {mentionQuery !== null && (
            <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 4, zIndex: 30 }}>
              <MentionDropdown users={teamUsers} query={mentionQuery} onSelect={handleMentionSelect} />
            </div>
          )}

          {/* ROW 1: Formatting toolbar */}
          {showFormatBar && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 1,
              padding: '4px 8px',
              borderBottom: '1px solid #e2e8f0',
            }}>
              {/* Bold */}
              <button className="fab-toolbar-btn" title="Bold" onClick={() => applyFormat('**', '**')}>
                <BoldIcon size={13} strokeWidth={1.8} />
              </button>
              {/* Italic */}
              <button className="fab-toolbar-btn" title="Italic" onClick={() => applyFormat('*', '*')}>
                <ItalicIcon size={13} strokeWidth={1.8} />
              </button>
              {/* Underline */}
              <button className="fab-toolbar-btn" title="Underline" onClick={() => applyFormat('__', '__')}>
                <UnderlineIcon size={13} strokeWidth={1.8} />
              </button>
              {/* Strikethrough */}
              <button className="fab-toolbar-btn" title="Strikethrough" onClick={() => applyFormat('~~', '~~')}>
                <Strikethrough size={13} strokeWidth={1.8} />
              </button>
              {/* Divider */}
              <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 3px', flexShrink: 0 }} />
              {/* Link */}
              <button className="fab-toolbar-btn" title="Link" onClick={() => applyFormat('[', '](url)')}>
                <Link size={13} strokeWidth={1.8} />
              </button>
              {/* Divider */}
              <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 3px', flexShrink: 0 }} />
              {/* Ordered list */}
              <button className="fab-toolbar-btn" title="Numbered list" onClick={() => applyFormat('1. ', '')}>
                <ListOrdered size={13} strokeWidth={1.8} />
              </button>
              {/* Unordered list */}
              <button className="fab-toolbar-btn" title="Bullet list" onClick={() => applyFormat('- ', '')}>
                <List size={13} strokeWidth={1.8} />
              </button>
              {/* Blockquote */}
              <button className="fab-toolbar-btn" title="Blockquote" onClick={() => applyFormat('> ', '')}>
                <Quote size={13} strokeWidth={1.8} />
              </button>
            </div>
          )}

          {/* ROW 2: Textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleTextareaInput}
            onKeyDown={e => handleKeyDown(e, showThread && !!threadParent)}
            placeholder={showThread && threadParent ? 'Reply in thread…' : active ? `Message #${active.display_name || active.name}` : 'Select a channel...'}
            disabled={!active || sending}
            rows={1}
            style={{
              display: 'block', width: '100%', boxSizing: 'border-box',
              border: 'none', outline: 'none', resize: 'none', overflow: 'hidden',
              padding: '8px 12px', fontSize: 16, fontFamily: 'inherit',
              background: 'transparent', color: tokens.color.text.body,
              lineHeight: '1.5', maxHeight: 120,
            }}
          />

          {/* ROW 2b: Thumbnail previews (when files selected) */}
          {pendingFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 10px 8px' }}>
              {pendingFiles.map((pf, i) => (
                <div key={i} style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', background: 'var(--bg-elevated)', flexShrink: 0 }}>
                  {/* Image preview */}
                  {pf.preview ? (
                    <img src={pf.preview} alt={pf.file.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    /* Video / other: dark bg + icon */
                    <div style={{ width: '100%', height: '100%', background: tokens.color.text.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Video size={28} strokeWidth={1.5} color="#94a3b8" />
                    </div>
                  )}
                  {/* Upload spinner overlay */}
                  {pf.uploading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'var(--bg-page)', borderRadius: '50%', animation: 'fabSpinner 0.7s linear infinite' }} />
                    </div>
                  )}
                  {/* Error overlay */}
                  {pf.error && !pf.uploading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(239,68,68,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--bg-page)', fontWeight: 600 }}>error</div>
                  )}
                  {/* Remove button */}
                  <button
                    onClick={() => {
                      if (pf.preview) URL.revokeObjectURL(pf.preview)
                      setPendingFiles(prev => prev.filter((_, j) => j !== i))
                    }}
                    style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, minHeight: 0, minWidth: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--bg-page)', fontSize: 10, lineHeight: 1 }}
                  >×</button>
                </div>
              ))}
            </div>
          )}

          {/* ROW 3: Action bar */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '3px 8px 5px',
            borderTop: pendingFiles.length > 0 ? '1px solid #e2e8f0' : '1px solid #e2e8f0',
            gap: 2,
          }}>
            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" multiple accept="*" style={{ display: 'none' }}
              onChange={e => { const files = Array.from(e.target.files || []); if (files.length) handleFileSelect(files); e.target.value = '' }} />
            <input ref={videoInputRef} type="file" multiple accept="video/*,image/*" style={{ display: 'none' }}
              onChange={e => { const files = Array.from(e.target.files || []); if (files.length) handleFileSelect(files); e.target.value = '' }} />

            {/* + all files */}
            <button className="fab-action-btn" title="Attach file" onClick={() => fileInputRef.current?.click()}>
              <Plus size={15} strokeWidth={1.8} />
            </button>

            {/* Aa formatting toggle */}
            <button
              className="fab-action-btn"
              title="Formatting"
              onClick={() => setShowFormatBar(v => !v)}
              style={{ color: showFormatBar ? 'var(--st-active)' : 'var(--text-secondary)', fontWeight: 600, fontSize: 12, fontFamily: 'inherit', padding: '4px 5px' }}
            >
              Aa
            </button>

            {/* Emoji */}
            <button className="fab-action-btn" title="Emoji">
              <Smile size={15} strokeWidth={1.8} />
            </button>

            {/* @ mention */}
            <button className="fab-action-btn" title="Mention someone"
              onClick={() => {
                const ta = textareaRef.current
                if (!ta) return
                const pos = ta.selectionStart
                const before = input.slice(0, pos)
                const after = input.slice(pos)
                setInput(before + '@' + after)
                setTimeout(() => {
                  ta.focus()
                  ta.setSelectionRange(pos + 1, pos + 1)
                  setMentionQuery('')
                }, 0)
              }}
            >
              <AtSign size={15} strokeWidth={1.8} />
            </button>

            {/* Video / media */}
            <button className="fab-action-btn" title="Attach media" onClick={() => videoInputRef.current?.click()}>
              <Video size={15} strokeWidth={1.8} />
            </button>

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Send */}
            <button
              onClick={() => handleSend(showThread && !!threadParent)}
              disabled={(!input.trim() && pendingFiles.filter(p => !p.uploading && p.uploadedUrl).length === 0) || !active || sending}
              style={{
                background: (input.trim() || pendingFiles.some(p => p.uploadedUrl)) && active ? 'var(--st-active)' : 'var(--border)',
                border: 'none', borderRadius: '50%', padding: 0, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                transition: 'background 0.15s', width: 30, height: 30, minHeight: 0, minWidth: 0,
              }}
            >
              <Send size={14} color={(input.trim() || pendingFiles.some(p => p.uploadedUrl)) && active ? 'var(--bg-page)' : 'var(--text-muted)'} />
            </button>
          </div>
        </div>
      </div>

      {/* Image / attachment lightbox — owned here (fixes #2018: MessageRow used
          to call a setPreviewAttachment that only existed in SageEmbed's scope). */}
      <AttachmentViewer attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
    </div>
  )
}
// ── Sage embed ─────────────────────────────────────────────────────────────────

const SESSION_KEY = 'sage_session_id'

type SageMsg = { id: number | string; role: 'user' | 'assistant' | 'system'; content: string; created_at: string }
type SageSession = { id: string; title: string; created_at: string; last_message_preview?: string; message_count?: number }

// ── Skeleton message placeholder (change 5) ────────────────────────────────────
const SkeletonMessage = ({ align }: { align: 'left' | 'right' }) => (
  <div style={{ display: 'flex', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
    <div style={{
      width: align === 'right' ? 160 : 200,
      height: 36,
      borderRadius: 12,
      background: 'linear-gradient(90deg, #1e1e1e 25%, #2a2a2a 50%, #1e1e1e 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite',
    }} />
  </div>
)

// ── Sage markdown + message rows (SAGE-SPEED-4) ────────────────────────────────
// All static: defined once at module scope so their identity is stable across
// SageEmbed renders. This is what lets the memoized rows/list below skip
// re-rendering (and re-parsing markdown) on every streamed token / keystroke.
const SAGE_MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => <p style={{ margin: '0 0 4px 0' }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 16 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 16 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  code: ({ children }) => <code style={{ fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-page)', padding: '1px 4px', borderRadius: 3 }}>{children}</code>,
}

// Shared markdown renderer for both committed assistant bubbles AND the live
// streaming bubble — so streaming text renders formatted (not plain) and there
// is no content reflow when the turn finalizes.
const SageMarkdown = memo(function SageMarkdown({ content }: { content: string }) {
  return <ReactMarkdown components={SAGE_MARKDOWN_COMPONENTS}>{content}</ReactMarkdown>
})

interface SageRowProps { msg: SageMsg; sageAvatar: string | null; brandColor: string; gold: string }

// One committed message. memo'd so a streamed token / keystroke elsewhere in
// SageEmbed does not re-render (or re-parse) already-settled bubbles.
const SageMessageRow = memo(function SageMessageRow({ msg, sageAvatar, brandColor, gold }: SageRowProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
      {msg.role === 'assistant' ? (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
          {sageAvatar ? (
            <img src={sageAvatar} style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }} alt="" />
          ) : (
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: brandColor, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--bg-page)' }}>✦</div>
          )}
          <div style={{
            maxWidth: '82%', padding: '7px 11px',
            borderRadius: '12px 12px 12px 2px',
            background: 'var(--bg-elevated)',
            color: tokens.color.text.body,
            fontSize: 12, lineHeight: 1.5,
            wordBreak: 'break-word', overflowWrap: 'break-word',
          }}>
            <SageMarkdown content={msg.content} />
          </div>
        </div>
      ) : (
        <div style={{
          maxWidth: '82%', padding: '7px 11px',
          borderRadius: '12px 12px 2px 12px',
          background: gold,
          color: 'var(--bg-page)',
          fontSize: 12, lineHeight: 1.5,
          wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap',
        }}>
          {msg.content}
        </div>
      )}
    </div>
  )
})

// The committed message list. memo'd on (messages, avatar, colors) — its props
// are stable during streaming/typing, so React skips the whole map + markdown
// pass and only the live streaming bubble updates per token.
const SageMessageList = memo(function SageMessageList({ messages, sageAvatar, brandColor, gold }: { messages: SageMsg[]; sageAvatar: string | null; brandColor: string; gold: string }) {
  return (
    <>
      {messages.map(msg => (
        <SageMessageRow key={msg.id} msg={msg} sageAvatar={sageAvatar} brandColor={brandColor} gold={gold} />
      ))}
    </>
  )
})

function SageEmbed({ mobileFullScreen = false, sageName = 'Sage', sageAvatar = null }: { mobileFullScreen?: boolean; sageName?: string; sageAvatar?: string | null }) {
  const location = useLocation()
  const params = useParams<{ id?: string; projectId?: string }>()
  const getPageContext = useCallback(() => {
    const ctx: Record<string, unknown> = { path: location.pathname }
    const projectId = params.projectId || params.id || location.pathname.match(/\/projects\/(\d+)/)?.[1]
    if (projectId) ctx.project_id = parseInt(projectId, 10)
    if (location.pathname.includes('/tasks')) ctx.page = 'tasks'
    else if (location.pathname.includes('/rfis')) ctx.page = 'rfis'
    else if (location.pathname.includes('/invoices')) ctx.page = 'invoices'
    else if (location.pathname.includes('/projects')) ctx.page = 'projects'
    else if (location.pathname.includes('/contacts')) ctx.page = 'contacts'
    else if (location.pathname.includes('/schedule')) ctx.page = 'schedule'
    else if (location.pathname.includes('/budget')) ctx.page = 'budget'
    else if (location.pathname.includes('/files')) ctx.page = 'files'
    return ctx
  }, [location.pathname, params])

  const [messages, setMessages] = useState<SageMsg[]>([])
  const [loading, setLoading] = useState(false)
  const [typing, setTyping] = useState(false)
  const [streamingContent, setStreamingContent] = useState<string | null>(null) // live chunk accumulator
  // activeTool — name of the tool Sage is currently invoking. Drives the
  // inline progress chip while a tool is running. Cleared on tool end/error.
  const [activeTool, setActiveTool] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loadingMessages, setLoadingMessages] = useState(true)
  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem(SESSION_KEY))
  const [showHistory, setShowHistory] = useState(false)
  const [sessions, setSessions] = useState<SageSession[]>([])
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  // Composer owns its own draft state (SAGE-SPEED-4) — typing no longer
  // re-renders SageEmbed / the message list. This ref lets us imperatively
  // prefill it (sage:open-compose) without lifting the draft back up.
  const composerRef = useRef<SageComposerHandle>(null)
  const sessionIdRef = useRef<string | null>(sessionId)
  sessionIdRef.current = sessionId
  // awaitingStreamRef: true while a direct (non-proxy) Anthropic stream is in flight.
  // Gates sage_response_chunk accumulation so stale chunks from prior turns or other
  // sessions don't bleed into the streaming bubble.
  const awaitingStreamRef = useRef(false)
  // handledByDirectStreamRef: true once the current turn is being reconciled
  // over the direct request stream (or its done/error frame). Lets the hub
  // sage_response handler skip its redundant full-history refetch (the flash
  // + double markdown re-parse the old code produced). Proxy turns leave it
  // false so the hub event still reconciles faster than the 10s poll.
  const handledByDirectStreamRef = useRef(false)

  const { branding } = useBranding()
  const brandColor = (branding.primary_color as string | undefined) || tokens.color.brand.gold
  const GOLD = tokens.color.brand.gold
  const agentName = sageName
  const [keyboardOffset, setKeyboardOffset] = useState(0)

  useEffect(() => {
    const kbHandler = () => {
      if (!window.visualViewport) return
      const kbH = Math.max(0, window.innerHeight - window.visualViewport.height - (window.visualViewport.offsetTop || 0))
      setKeyboardOffset(kbH)
    }
    window.visualViewport?.addEventListener('resize', kbHandler)
    window.visualViewport?.addEventListener('scroll', kbHandler)
    return () => {
      window.visualViewport?.removeEventListener('resize', kbHandler)
      window.visualViewport?.removeEventListener('scroll', kbHandler)
    }
  }, [mobileFullScreen])

  // sage:open-compose — prefills the composer draft with the provided message
  // (dispatched by e.g. the FS RejectionCard "Draft response" button). The
  // draft now lives inside SageComposer, so we push it in imperatively.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail
      if (detail?.message) composerRef.current?.setText(detail.message)
    }
    window.addEventListener('sage:open-compose', handler)
    return () => window.removeEventListener('sage:open-compose', handler)
  }, [])

  // sage-soul (display name + avatar) is fetched once by the parent ChatFABPanel
  // and passed down as props — no second fetch here (SAGE-SPEED-4).

  const isAtBottom = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < 50
  }, [])

  // rAF-coalesced scroll. Streaming used to queue a smooth scrollIntoValue per
  // token (dozens/sec) — janky + expensive. Now each request cancels the prior
  // and runs at most once per frame; `instant` (behavior:'auto') during
  // streaming avoids the smooth-animation pile-up.
  const scrollRafRef = useRef<number | null>(null)
  const scrollToBottom = useCallback((opts?: { force?: boolean; instant?: boolean }) => {
    const force = opts?.force ?? false
    const instant = opts?.instant ?? false
    if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current)
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null
      if (force || isAtBottom()) {
        messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' })
      }
    })
  }, [isAtBottom])
  useEffect(() => () => { if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current) }, [])

  const filterMessages = (msgs: SageMsg[]) =>
    msgs.filter(m => m.role !== 'system' && !m.content.startsWith('HEARTBEAT_OK') && m.content !== 'NO_REPLY')

  const fetchMessages = useCallback(async (sid?: string | null) => {
    const activeSid = sid !== undefined ? sid : sessionIdRef.current
    if (!activeSid) return
    const r = await authedFetch(`/api/v1/sage/messages?session_id=${activeSid}`, {}, { redirectOnAuthFailure: false }).catch(() => null)
    if (!r?.ok) return
    const data = await r.json() as { messages: SageMsg[] }
    if (data?.messages) {
      setMessages(filterMessages(data.messages))
      setTimeout(() => scrollToBottom(), 50)
    }
  }, [scrollToBottom])

  useEffect(() => {
    const init = async () => {
      try {
        let sid = localStorage.getItem(SESSION_KEY)
        if (!sid) {
          const r = await authedFetch('/api/v1/sage/sessions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: 'New Chat' }),
          }, { redirectOnAuthFailure: false }).catch(() => null)
          if (!r?.ok) return
          const data = await r.json() as { id: string }
          sid = data.id
          localStorage.setItem(SESSION_KEY, sid)
          setSessionId(sid)
        } else {
          setSessionId(sid)
        }
        await fetchMessages(sid)
      } catch { /* ignore */ } finally {
        setLoadingMessages(false)
      }
    }
    init()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle incoming streaming chunks — append to live bubble.
  // awaitingStreamRef gates this so only chunks from an active direct stream
  // are shown; stale events from prior turns are silently dropped.
  useSSEEvent('sage_response_chunk', useCallback((event) => {
    if (!awaitingStreamRef.current) return
    const payload = event.payload as { message_id?: number; chunk?: string; session_id?: string }
    if (!payload?.chunk) return
    setStreamingContent(prev => (prev ?? '') + payload.chunk)
    scrollToBottom({ force: true, instant: true })
  }, [scrollToBottom]))

  // sage_response fires when full response is ready — clear streaming buffer + refresh.
  // When the turn was reconciled over the direct request stream (its done frame
  // already set the final messages), skip the refetch — it only caused a
  // redundant full-history reload + markdown re-parse + visible flash.
  useSSEEvent('sage_response', useCallback(() => {
    awaitingStreamRef.current = false
    setStreamingContent(null)
    setActiveTool(null)
    setTyping(false)
    if (handledByDirectStreamRef.current) return
    fetchMessages()
  }, [fetchMessages]))

  useSSEEvent('sage_proxy_response', useCallback(() => {
    awaitingStreamRef.current = false
    setStreamingContent(null)
    setActiveTool(null)
    fetchMessages()
  }, [fetchMessages]))

  // Tool-call lifecycle from the streaming backend — drives the active-tool chip.
  useSSEEvent('sage_tool_call', useCallback((event) => {
    if (!awaitingStreamRef.current) return
    const payload = event.payload as { name?: string; status?: string }
    if (!payload?.name || !payload?.status) return
    if (payload.status === 'start') {
      setActiveTool(payload.name)
    } else {
      setActiveTool(prev => (prev === payload.name ? null : prev))
    }
  }, []))

  const handleNewChat = useCallback(async () => {
    const r = await authedFetch('/api/v1/sage/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: 'New Chat' }),
    }, { redirectOnAuthFailure: false }).catch(() => null)
    if (!r?.ok) return
    const data = await r.json() as { id: string }
    localStorage.setItem(SESSION_KEY, data.id)
    setSessionId(data.id)
    setMessages([])
    setError(null)
    setTyping(false)
    setLoading(false)
    setShowHistory(false)
  }, [])

  const loadSessions = useCallback(async () => {
    const r = await authedFetch('/api/v1/sage/sessions', {}, { redirectOnAuthFailure: false }).catch(() => null)
    if (!r?.ok) return
    const data = await r.json() as { sessions: SageSession[] }
    setSessions(data.sessions || [])
  }, [])

  useEffect(() => { if (showHistory) loadSessions() }, [showHistory, loadSessions])

  const resumeSession = useCallback(async (sid: string) => {
    localStorage.setItem(SESSION_KEY, sid)
    setSessionId(sid)
    setShowHistory(false)
    await fetchMessages(sid)
  }, [fetchMessages])

  const deleteSession = useCallback(async (sid: string) => {
    await authedFetch(`/api/v1/sage/sessions/${sid}`, {
      method: 'DELETE',
    }, { redirectOnAuthFailure: false }).catch(() => null)
    if (sid === sessionIdRef.current) {
      localStorage.removeItem(SESSION_KEY)
      setSessionId(null)
      setMessages([])
    }
    loadSessions()
  }, [loadSessions])

  // Draft text is owned by SageComposer now; both the composer's Send/Enter and
  // the voice-note flow call this with the resolved content string. Not
  // depending on any `input` state keeps sendMessage's identity stable (fewer
  // re-created closures) and means typing never re-renders SageEmbed.
  const sendMessage = useCallback(async (raw: string) => {
    const content = (raw ?? '').trim()
    if (!content || loading) return

    let sid = sessionIdRef.current
    if (!sid) {
      const r = await authedFetch('/api/v1/sage/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'New Chat' }),
      }, { redirectOnAuthFailure: false }).catch(() => null)
      if (!r?.ok) return
      const data = await r.json() as { id: string }
      sid = data.id
      localStorage.setItem(SESSION_KEY, sid)
      setSessionId(sid)
    }

    const optimistic: SageMsg = {
      id: `opt-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, optimistic])
    // Mark that we're awaiting a direct Anthropic stream so sage_response_chunk
    // events accumulate in the streaming bubble. Cleared on response or error.
    awaitingStreamRef.current = true
    // Reset the direct-stream guard; set true only once we confirm this turn is
    // being reconciled over the request stream (see the event-stream branch).
    handledByDirectStreamRef.current = false
    setStreamingContent(null)
    setActiveTool(null)
    setLoading(true)
    setTyping(true)
    setError(null)
    setTimeout(() => scrollToBottom({ force: true }), 50)

    let proxyMode = false

    try {
      const res = await authedFetch('/api/v1/sage/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify({ content, session_id: sid, context: getPageContext() }),
      }, { redirectOnAuthFailure: false })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      // SAGE-SPEED-2: when the backend streams the turn over the request
      // itself, read our OWN ordered/lossless stream instead of the drop-prone
      // global hub. Hub chunk events are ignored here (awaitingStreamRef=false)
      // so a second tab's tokens can't bleed into this bubble.
      const contentType = res.headers.get('content-type') || ''
      if (contentType.includes('text/event-stream') && res.body) {
        awaitingStreamRef.current = false
        // This turn reconciles over the request stream — tell the hub
        // sage_response handler to skip its redundant refetch.
        handledByDirectStreamRef.current = true
        let acc = ''
        let finalized = false
        await readSageStream(res.body, (evt) => {
          const d = evt.data as Record<string, unknown>
          switch (evt.event) {
            case 'meta': {
              const metaSid = d?.session_id as string | undefined
              if (metaSid && metaSid !== sessionIdRef.current) {
                localStorage.setItem(SESSION_KEY, metaSid)
                setSessionId(metaSid)
                sid = metaSid
              }
              const um = d?.user_message as SageMsg | undefined
              if (um) setMessages(prev => prev.map(m => (m.id === optimistic.id ? um : m)))
              break
            }
            case 'delta': {
              const chunk = d?.chunk as string | undefined
              if (chunk) {
                acc += chunk
                setStreamingContent(acc)
                scrollToBottom({ force: true, instant: true })
              }
              break
            }
            case 'tool': {
              const name = d?.name as string | undefined
              const status = d?.status as string | undefined
              if (name && status === 'start') setActiveTool(name)
              else if (name) setActiveTool(prev => (prev === name ? null : prev))
              break
            }
            case 'done': {
              // A connection severed mid-frame makes readSageStream's end flush
              // fabricate a terminal frame whose truncated JSON parses to a raw
              // string, not an object. Don't finalize on that — leave finalized
              // false so the !finalized reconcile below refetches the (already
              // persisted) reply instead of silently dropping it.
              if (typeof d !== 'object' || d === null) break
              finalized = true
              const um = d?.user_message as SageMsg | undefined
              const resp = d?.response as SageMsg | undefined
              const doneSid = d?.session_id as string | undefined
              if (doneSid && doneSid !== sessionIdRef.current) {
                localStorage.setItem(SESSION_KEY, doneSid)
                setSessionId(doneSid)
                sid = doneSid
              }
              setStreamingContent(null)
              setActiveTool(null)
              setMessages(prev => {
                const without = prev.filter(m => m.id !== optimistic.id && m.id !== um?.id)
                const next = [...without]
                if (um) next.push(um)
                if (resp) next.push(resp)
                return next
              })
              break
            }
            case 'error': {
              if (typeof d !== 'object' || d === null) break // truncated frame — let the reconcile fire
              finalized = true
              setStreamingContent(null)
              setActiveTool(null)
              setError('Failed to send. Try again.')
              break
            }
          }
        })
        // Stream ended without a terminal frame (e.g. dropped connection) —
        // reconcile from the server so a delivered answer isn't lost.
        if (!finalized) {
          setStreamingContent(null)
          setActiveTool(null)
          await fetchMessages(sid)
        }
        return
      }

      // Non-streaming backend / caller — buffered JSON envelope (unchanged).
      const data = await res.json() as {
        user_message: SageMsg
        response?: SageMsg
        proxy_pending?: boolean
        session_id?: string
      }

      if (data.session_id && data.session_id !== sessionIdRef.current) {
        localStorage.setItem(SESSION_KEY, data.session_id)
        setSessionId(data.session_id)
        sid = data.session_id
      }

      if (data.proxy_pending) {
        // Proxy mode: no direct Anthropic stream — clear the awaiting flag
        awaitingStreamRef.current = false
        proxyMode = true
        setLoading(false)
        setMessages(prev => [...prev.filter(m => m.id !== optimistic.id), data.user_message])
        setTyping(true)

        const userMsgTime = data.user_message?.created_at
          ? new Date(data.user_message.created_at).getTime()
          : Date.now()
        const activeSid = data.session_id || sid
        const startTime = Date.now()

        const poll = setInterval(async () => {
          if (Date.now() - startTime > 90000) {
            clearInterval(poll)
            setTyping(false)
            setError('No response yet. Try sending again.')
            return
          }
          const pr = await authedFetch(`/api/v1/sage/messages?session_id=${activeSid}`, {}, { redirectOnAuthFailure: false }).catch(() => null)
          if (!pr?.ok) return
          const pd = await pr.json() as { messages: SageMsg[] }
          const newAss = (pd.messages || []).find(
            m => m.role === 'assistant' && new Date(m.created_at).getTime() > userMsgTime
          )
          if (newAss) {
            clearInterval(poll)
            setTyping(false)
            setStreamingContent(null)
            setActiveTool(null)
            setMessages(filterMessages(pd.messages || []))
            setTimeout(() => scrollToBottom(), 50)
          }
        }, 10000)
        return
      }

      // Direct (non-proxy) buffered response landed — stop accumulating chunks +
      // show full message. Reconciled here, so suppress the hub sage_response
      // refetch (same anti-flash guard as the streaming path).
      awaitingStreamRef.current = false
      handledByDirectStreamRef.current = true
      setStreamingContent(null)
      setActiveTool(null)
      setMessages(prev => {
        const without = prev.filter(m => m.id !== optimistic.id)
        if (data.response) return [...without, data.user_message, data.response]
        return [...without, data.user_message]
      })
    } catch {
      awaitingStreamRef.current = false
      setStreamingContent(null)
      setActiveTool(null)
      setError('Failed to send. Try again.')
      setMessages(prev => prev.filter(m => m.id !== optimistic.id))
    } finally {
      if (!proxyMode) {
        setLoading(false)
        setTyping(false)
      }
    }
  }, [loading, scrollToBottom])

  // Memoized so its identity is stable across streamed-token / keystroke
  // renders — that's what lets the memoized <SageMessageList> below skip
  // re-rendering (and re-parsing every markdown bubble) per token.
  const displayMessages = useMemo(
    () => messages.filter(m => m.role !== 'system' && !m.content.startsWith('HEARTBEAT_OK') && m.content !== 'NO_REPLY'),
    [messages],
  )

  if (showHistory) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: `1px solid ${tokens.color.border.default}` }}>
        <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', color: tokens.color.text.muted, cursor: 'pointer', padding: 4, display: 'flex' }}><ChevronLeft size={16} /></button>
        <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: GOLD }}>Chat History</span>
        <button onClick={handleNewChat} style={{ background: 'none', border: 'none', color: tokens.color.text.muted, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, minWidth: 44, minHeight: 44, justifyContent: 'center' }}><PenSquare size={13} /> New</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0 }}>
        {sessions.length === 0 && <div style={{ color: tokens.color.text.muted, fontSize: 12, textAlign: 'center', marginTop: 20 }}>No past chats</div>}
        {sessions.map(s => (
          <div
            key={s.id}
            onClick={() => resumeSession(s.id)}
            style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${s.id === sessionId ? GOLD : tokens.color.border.default}`, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: tokens.color.text.body, marginBottom: 2 }}>{s.title}</div>
              {s.last_message_preview && <div style={{ fontSize: 11, color: tokens.color.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.last_message_preview}</div>}
              <div style={{ fontSize: 10, color: tokens.color.text.disabled, marginTop: 2 }}>{formatDate(s.created_at)}</div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); deleteSession(s.id) }}
              style={{ background: 'none', border: 'none', color: tokens.color.text.disabled, cursor: 'pointer', padding: 2, flexShrink: 0, display: 'flex' }}
            ><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`
        @keyframes sageTyping{0%,60%,100%{opacity:0.3;transform:translateY(0)}30%{opacity:1;transform:translateY(-3px)}}
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes sageCursor{0%,100%{opacity:1}50%{opacity:0}}
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: `1px solid ${tokens.color.border.default}`, flexShrink: 0 }}>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)' }}>{agentName}</span>
        <button onClick={handleNewChat} title="New chat" style={{ background: 'none', border: 'none', color: tokens.color.text.muted, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44 }}><PenSquare size={13} /></button>
        <button onClick={() => setShowHistory(true)} title="Chat history" style={{ background: 'none', border: 'none', color: tokens.color.text.muted, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44 }}><History size={13} /></button>
      </div>

      <div
        ref={messagesContainerRef}
        style={{
          flex: 1, overflowY: 'auto', padding: '10px 12px',
          display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0,
          WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'],
        }}
      >
        {loadingMessages && messages.length === 0 && (
          <>
            <SkeletonMessage align="left" />
            <SkeletonMessage align="right" />
            <SkeletonMessage align="left" />
          </>
        )}
        {!loadingMessages && displayMessages.length === 0 && !typing && (
          <div style={{ textAlign: 'center', color: tokens.color.text.muted, fontSize: 12, marginTop: 32 }}>
            Hi! I'm {agentName}. Ask me anything.
          </div>
        )}
        {/* Committed messages — memoized list; per-token/keystroke renders skip
            this entirely (stable props) so only the streaming bubble updates. */}
        <SageMessageList messages={displayMessages} sageAvatar={sageAvatar} brandColor={brandColor} gold={GOLD} />
        {/* Active-tool chip — shows which tool Sage is invoking right now */}
        {activeTool && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 4 }}>
            <div style={{
              padding: '5px 10px',
              borderRadius: '10px 10px 10px 2px',
              background: 'var(--brand-gold-quiet)',
              border: '1px solid var(--brand-gold-border)',
              fontSize: 11,
              color: GOLD,
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span style={{
                display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                background: GOLD, animation: 'sageCursor 0.8s step-end infinite',
              }} />
              <span>Calling {activeTool.replace(/_/g, ' ')}…</span>
            </div>
          </div>
        )}

        {/* Streaming bubble — shows live chunks arriving */}
        {streamingContent && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
              {sageAvatar ? (
                <img src={sageAvatar} style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }} alt="" />
              ) : (
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: brandColor, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--bg-page)' }}>✦</div>
              )}
              {/* Same bubble styles + SageMarkdown as the committed assistant
                  row, so the turn finalizes with no content reflow (SAGE-SPEED-4:
                  streaming now renders formatted markdown, not plain text). */}
              <div style={{
                maxWidth: '82%', padding: '7px 11px',
                borderRadius: '12px 12px 12px 2px',
                background: 'var(--bg-elevated)',
                color: tokens.color.text.body,
                fontSize: 12, lineHeight: 1.5,
                wordBreak: 'break-word', overflowWrap: 'break-word',
              }}>
                <SageMarkdown content={streamingContent} />
                <span style={{
                  display: 'inline-block', width: 2, height: '1em',
                  background: 'currentColor', marginLeft: 2, verticalAlign: 'text-bottom',
                  animation: 'sageCursor 0.8s step-end infinite',
                  opacity: 0.7,
                }} />
              </div>
            </div>
          </div>
        )}
        {typing && !streamingContent && !activeTool && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '8px 12px', borderRadius: '12px 12px 12px 2px', background: 'var(--bg-elevated)', display: 'flex', gap: 4, alignItems: 'center' }}>
              {[0,1,2].map(i => (
                <span key={i} style={{
                  width: 5, height: 5, borderRadius: '50%', background: tokens.color.text.muted,
                  display: 'inline-block', animation: 'sageTyping 1.2s infinite', animationDelay: `${i * 0.2}s`,
                }} />
              ))}
            </div>
          </div>
        )}
        {error && <div style={{ textAlign: 'center', color: 'var(--st-error)', fontSize: 11 }}>{error}</div>}
        <div ref={messagesEndRef} />
      </div>

      <div style={{
        padding: '8px 10px',
        // mobileFullScreen: ChatFABPanel already lifts above nav + keyboard — no double offset here
        paddingBottom: keyboardOffset > 0 && !mobileFullScreen
          ? `${keyboardOffset + 8}px`
          : mobileFullScreen
            ? '8px'
            : 'max(8px, env(safe-area-inset-bottom))',
        borderTop: `1px solid ${tokens.color.border.default}`,
        flexShrink: 0,
      }}>
        <SageComposer
          ref={composerRef}
          onSend={sendMessage}
          loading={loading}
          placeholder={`Ask ${agentName}...`}
          brandColor={brandColor}
          onVoiceNoteTranscript={(text) => {
            // Fire-and-forget — dispatch directly through sendMessage so the
            // transcript never touches the composer draft.
            void sendMessage(text)
          }}
          onConversationStart={() => {
            window.dispatchEvent(new CustomEvent('sage:conversation-start'))
          }}
        />
      </div>
    </div>
  )
}

// ── Main ChatFABPanel ─────────────────────────────────────────────────────────

interface ChatFABPanelProps {
  unreadCount?: number
  onMessagesRead?: () => void
  open?: boolean
  onClose?: () => void
  hideFab?: boolean
}

export const ChatFABPanel: React.FC<ChatFABPanelProps> = ({ unreadCount = 0, open: controlledOpen, onClose, hideFab = false, onMessagesRead }) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof value === 'function' ? value(open) : value
    setInternalOpen(next)
    if (!next) onClose?.()
  }
  const [mode, setMode] = useState<PanelMode>('compact')
  // Sprint 4 Sage P? — persistent docked sidebar mode. When true, the
  // expanded panel stays open + main content reflows around it via
  // body padding-right, the FAB hides, and the dim overlay disappears.
  const [docked, setDocked] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('sage_fab_docked') === 'true'
  })
  const [previewAttachment, setPreviewAttachment] = useState<ViewerAttachment | null>(null)
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem('sage_fab_docked', String(docked))
  }, [docked])
  const [activeTab, setActiveTab] = useState<Tab>('sage')
  const [drafts, setDrafts] = useState<any[]>([])
  const [draftsCount, setDraftsCount] = useState(0)
  const [fabHovered, setFabHovered] = useState(false)
  const [conversationMode, setConversationMode] = useState(false)
  const [sageName, setSageName] = useState('Sage')
  const [sageAvatar, setSageAvatar] = useState<string | null>(null)
  const [compactHeight, setCompactHeight] = useState(480)
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const [mobileKeyboardH, setMobileKeyboardH] = useState(0)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640)
  const { branding } = useBranding()
  const brandColor = branding.primary_color || tokens.color.brand.gold

  useEffect(() => {
    const resizeHandler = () => setIsMobile(window.innerWidth < 640)
    window.addEventListener('resize', resizeHandler)
    return () => window.removeEventListener('resize', resizeHandler)
  }, [])

  // SageComposer dispatches `sage:conversation-start` when the user taps the
  // conversation-mode button. The FAB slot transforms into the conversation
  // card in-place (no separate overlay). If the chat panel is open, we
  // close it so the FAB slot is visible for the card to occupy.
  useEffect(() => {
    const handler = () => {
      setConversationMode(true)
      setOpen(false)
    }
    window.addEventListener('sage:conversation-start', handler)
    return () => window.removeEventListener('sage:conversation-start', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // sage:open-compose — opens the FAB panel on the Sage tab with a prefilled
  // message. Fired by surfaces that want to hand off context to Sage (e.g.
  // the FS RejectionCard "Draft response" button). SageEmbed listens for the
  // same event to set its input state.
  useEffect(() => {
    const handler = () => {
      setOpen(true)
      setActiveTab('sage')
    }
    window.addEventListener('sage:open-compose', handler)
    return () => window.removeEventListener('sage:open-compose', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handler = () => {
      if (!window.visualViewport) return
      const kbH = Math.max(0, window.innerHeight - window.visualViewport.height - (window.visualViewport.offsetTop || 0))
      setKeyboardOpen(kbH > 150)
      setMobileKeyboardH(kbH)
    }
    window.visualViewport?.addEventListener('resize', handler)
    window.visualViewport?.addEventListener('scroll', handler)
    return () => {
      window.visualViewport?.removeEventListener('resize', handler)
      window.visualViewport?.removeEventListener('scroll', handler)
    }
  }, [])

  // Capacitor Keyboard plugin — overrides visualViewport when resize:'body' is
  // configured (body-only resize doesn't shrink the visual viewport, so the
  // formula above yields 0; native events carry the real keyboard height).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let cancelled = false
    const handles: { remove: () => void }[] = []
    Promise.all([
      Keyboard.addListener('keyboardWillShow', ({ keyboardHeight }) => {
        if (cancelled) return
        setKeyboardOpen(true)
        setMobileKeyboardH(keyboardHeight)
      }),
      Keyboard.addListener('keyboardWillHide', () => {
        if (cancelled) return
        setKeyboardOpen(false)
        setMobileKeyboardH(0)
      }),
    ]).then(hs => { handles.push(...hs) }).catch(() => {})
    return () => {
      cancelled = true
      handles.forEach(h => h.remove())
    }
  }, [])

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sage-keyboard', { detail: { open: keyboardOpen } }))
  }, [keyboardOpen])

  useEffect(() => {
    authedFetch('/api/v1/settings/sage-soul', {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.display_name) setSageName(d.display_name)
        if (d?.avatar_url) setSageAvatar(d.avatar_url)
      })
      .catch(() => {})
  }, [])

  const fetchDrafts = useCallback(() => {
    authedFetch('/api/v1/sage/drafts?status=pending', {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.drafts) { setDrafts(d.drafts); setDraftsCount(d.drafts.length) } })
      .catch(() => {})
  }, [])

  useEffect(() => { fetchDrafts() }, [fetchDrafts])

  // Desktop expanded == side-docked: the shell resizes beside the panel, so
  // it must NOT dim, lock page scroll, dismiss on click-out, or close on Esc.
  const sideDocked = !isMobile && open && mode === 'expanded'

  useEffect(() => {
    // Esc closes only when not docked. When docked, the panel is meant
    // to stay open; the user undocks via the Pin or X button.
    if (!open || docked || sideDocked) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, docked, sideDocked])

  useEffect(() => {
    // Body overflow lock only applies when the panel is in floating-overlay
    // mode (i.e. NOT docked). Docked mode is sticky inline space — content
    // can scroll freely.
    if (open && !docked && !sideDocked) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open, docked, sideDocked])

  // Docked mode: panel is permanently visible (open=true, expanded) and the
  // app shell RESIZES beside it — the page is never overlaid or hidden under
  // the panel, and stays fully interactive (no backdrop, no click-out).
  //
  // This publishes the dock width as a CSS custom property on :root; the shell
  // (SidebarLayout) consumes it via `width: calc(100% - var(--chat-dock-w))`.
  // The previous implementation set `document.body.style.paddingRight`, which
  // was a NO-OP: the app shell is `height:100vh; overflow:hidden` and does not
  // participate in body box flow, so content kept its full width and simply sat
  // underneath the docked panel (the bug Joe reported 2026-08-07 — "the viewport
  // shouldn't be below the sidepeek, it should be resized beside it").
  // Mobile never docks (no room).
  //
  // 2026-08-07 (Joe, round 2): this reflow now applies to ANY open EXPANDED
  // panel on desktop, not just the explicit `docked` pin. Expanding is the
  // action Joe actually takes ("when I expand the chat window it dims the rest
  // of the page and disappears when I click out") — gating the reflow behind the
  // separate, undiscoverable pin button meant expanding still overlaid the page
  // and clipped content under the panel. On desktop, expanded IS side-docked:
  // page resizes beside it, no backdrop, no click-out dismiss. The pin now only
  // controls STICKINESS (survives close/reopen + Esc), not the layout behaviour.
  useEffect(() => {
    const root = document.documentElement
    if (docked && !isMobile) {
      setOpen(true)
      setMode('expanded')
    }
    if (sideDocked || (docked && !isMobile)) {
      root.style.setProperty('--chat-dock-w', `${DOCK_WIDTH}px`)
    } else {
      root.style.removeProperty('--chat-dock-w')
    }
    return () => { root.style.removeProperty('--chat-dock-w') }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docked, isMobile, sideDocked])

  useEffect(() => {
    if (!open || mode !== 'compact') return
    // On mobile, height is handled by mobileOverride (fixed inset 0) — skip dynamic height
    if (isMobile) return
    const FAB_BOTTOM = 24
    const FAB_SIZE = 52
    const PANEL_TOP_OFFSET = FAB_BOTTOM + FAB_SIZE + 12

    const handler = () => {
      const vv = window.visualViewport
      if (!vv) return
      const available = vv.height - PANEL_TOP_OFFSET - 8
      setCompactHeight(Math.min(480, Math.max(200, available)))
    }

    window.visualViewport?.addEventListener('resize', handler)
    handler()
    return () => window.visualViewport?.removeEventListener('resize', handler)
  }, [open, mode, isMobile])

  useEffect(() => {
    if (!open || mode !== 'compact') setCompactHeight(480)
  }, [open, mode])

  const reviewDraft = async (id: number, status: 'approved' | 'rejected') => {
    await authedFetch(`/api/v1/sage/drafts/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }, { redirectOnAuthFailure: false })
    fetchDrafts()
  }

  const FAB_BOTTOM = 24
  const FAB_RIGHT = 24
  const FAB_SIZE = 52

  const compactStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: FAB_BOTTOM + FAB_SIZE + 12,
    right: FAB_RIGHT,
    width: 360,
    height: compactHeight,
    zIndex: 201,
    background: tokens.color.surface.surface,
    border: `1px solid ${tokens.color.border.default}`,
    borderRadius: 14,
    boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transform: open ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(10px)',
    opacity: open ? 1 : 0,
    pointerEvents: open ? 'all' : 'none',
    transition: 'transform 0.2s cubic-bezier(0.4,0,0.2,1), opacity 0.2s',
    transformOrigin: 'bottom right',
  }

  const expandedStyle: React.CSSProperties = {
    position: 'fixed',
    right: 0,
    top: 0,
    bottom: 0,
    width: DOCK_WIDTH,
    zIndex: 201,
    background: tokens.color.surface.surface,
    borderLeft: `1px solid ${tokens.color.border.default}`,
    // No drop shadow while side-docked: the panel is inline layout space beside
    // the resized page, not a floating overlay — a 32px shadow reads as a modal
    // hovering over the app (Joe, 2026-08-07). The 1px borderLeft is enough.
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transform: open ? 'translateX(0)' : 'translateX(100%)',
    opacity: open ? 1 : 0,
    pointerEvents: open ? 'all' : 'none',
    transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.2s',
  }

  // MobileNav is position:fixed, z-index:202. Due to -webkit-overflow-scrolling
  // on the parent scroll container creating a stacking context on iOS, the
  // panel can appear behind the nav bar even at z-index:9999. Fix: always stop
  // the panel above the nav + safe-area so the composer is never obscured.
  const MOBILE_NAV_H = 78
  const mobileOverride: React.CSSProperties = isMobile ? {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: mobileKeyboardH > 0
      ? mobileKeyboardH
      : `calc(${MOBILE_NAV_H}px + env(safe-area-inset-bottom, 0px))`,
    width: '100%',
    height: mobileKeyboardH > 0
      ? `calc(100% - ${mobileKeyboardH}px)`
      : `calc(100% - ${MOBILE_NAV_H}px - env(safe-area-inset-bottom, 0px))`,
    maxHeight: 'none',
    borderRadius: 0,
    zIndex: 9999,
    transform: 'none',
    opacity: 1,
    transition: mobileKeyboardH > 0 ? 'bottom 0.15s ease, height 0.15s ease' : 'none',
  } : {}
  const panelStyle = mode === 'compact'
    ? { ...compactStyle, ...(isMobile && open ? mobileOverride : {}) }
    : { ...expandedStyle, ...(isMobile && open ? mobileOverride : {}) }

  return (
    <>
      {/* Panel */}
      <div style={panelStyle} role="dialog" aria-label="Chat panel">
        {/* Header: tabs + controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${tokens.color.border.default}`,
          background: tokens.color.surface.surface,
          flexShrink: 0,
          padding: isMobile ? 'env(safe-area-inset-top, 0px) 8px 0 4px' : '0 8px 0 4px',
          minHeight: isMobile ? 56 : 44,
          gap: 4,
        }}>
          <div style={{ display: 'flex', flex: 1, gap: 2 }}>
            <button
                key="sage"
                onClick={() => setActiveTab('sage')}
                style={{
                  padding: isMobile ? '8px 10px' : '6px 12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: isMobile ? 15 : 13,
                  fontWeight: activeTab === 'sage' ? 700 : 400,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'sage' ? brandColor : tokens.color.text.secondary,
                  borderBottom: activeTab === 'sage' ? `2px solid ${brandColor}` : '2px solid transparent',
                  marginBottom: -1,
                  transition: 'color 0.15s',
                  fontFamily: tokens.typography.fontFamily.sans,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {sageAvatar ? (
                    <img src={sageAvatar} style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0 }} alt="" />
                  ) : (
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: brandColor, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 }}>✦</span>
                  )}
                  {sageName}
                </span>
              </button>
              {!isMobile && (
              <button
                key="team"
                onClick={() => setActiveTab('team')}
                style={{
                  padding: '6px 12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: activeTab === 'team' ? 700 : 400,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'team' ? brandColor : tokens.color.text.secondary,
                  borderBottom: activeTab === 'team' ? `2px solid ${brandColor}` : '2px solid transparent',
                  marginBottom: -1,
                  transition: 'color 0.15s',
                  fontFamily: tokens.typography.fontFamily.sans,
                }}
              >
                Team Chat
              </button>
              )}
              {!isMobile && (
              <button
                key="drafts"
                onClick={() => setActiveTab('drafts')}
                style={{
                  padding: '6px 12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: activeTab === 'drafts' ? 700 : 400,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'drafts' ? brandColor : tokens.color.text.secondary,
                  borderBottom: activeTab === 'drafts' ? `2px solid ${brandColor}` : '2px solid transparent',
                  marginBottom: -1,
                  transition: 'color 0.15s',
                  fontFamily: tokens.typography.fontFamily.sans,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                Drafts
                {draftsCount > 0 && (
                  <span style={{ background: 'var(--st-error)', color: 'var(--bg-page)', borderRadius: '50%', width: 16, height: 16, fontSize: 9, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {draftsCount}
                  </span>
                )}
              </button>
              )}
          </div>
          {!isMobile && (
          <button
            onClick={() => setMode(m => m === 'compact' ? 'expanded' : 'compact')}
            title={mode === 'compact' ? 'Expand' : 'Minimize'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: tokens.color.text.muted, padding: 4, display: 'flex', borderRadius: 4 }}
          >
            {mode === 'compact' ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
          </button>
          )}
          {!isMobile && mode === 'expanded' && (
          <button
            onClick={() => setDocked(d => !d)}
            title={docked ? 'Undock — return to floating panel' : 'Dock — keep panel persistently open'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: docked ? brandColor : tokens.color.text.muted,
              padding: 4,
              display: 'flex',
              borderRadius: 4,
            }}
          >
            <Pin size={14} style={{ transform: docked ? 'rotate(0deg)' : 'rotate(45deg)', transition: 'transform 0.15s' }} />
          </button>
          )}
          <button
            onClick={() => { if (docked) setDocked(false); setOpen(false) }}
            aria-label="Close panel"
            title={docked ? 'Undock and close' : 'Close panel'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: tokens.color.text.muted, padding: isMobile ? 10 : 4, display: 'flex', borderRadius: 4, minWidth: isMobile ? 44 : 'auto', minHeight: isMobile ? 44 : 'auto', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={isMobile ? 22 : 15} />
          </button>
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 0 }}>
          {/* SageEmbed stays mounted across tab switches (hidden, not
              unmounted) so its conversation state + streaming aren't destroyed
              and re-fetched every time the user peeks at Team/Drafts
              (SAGE-SPEED-4). */}
          <div style={{ height: '100%', display: activeTab === 'sage' ? 'block' : 'none' }}>
            <SageEmbed mobileFullScreen={isMobile} sageName={sageName} sageAvatar={sageAvatar} />
          </div>
          {activeTab === 'team' && <TeamChatEmbed mobileFullScreen={isMobile} onRead={onMessagesRead} />}
          {activeTab === 'drafts' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px', height: '100%' }}>
              {drafts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: tokens.color.text.muted, fontSize: 12 }}>No drafts pending review</div>
              ) : drafts.map(draft => (
                <div key={draft.id} style={{ background: tokens.color.background.elevated, borderRadius: 8, padding: '12px', marginBottom: 8, border: `1px solid ${tokens.color.border.default}` }}>
                  <div style={{ fontSize: 11, color: tokens.color.text.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{draft.skill_slug?.replace(/_/g, ' ')}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: tokens.color.text.body, marginBottom: 6 }}>{draft.title}</div>
                  {draft.content && <div style={{ fontSize: 12, color: tokens.color.text.secondary, marginBottom: 10, lineHeight: 1.5, maxHeight: 60, overflow: 'hidden' }}>{draft.content}</div>}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => reviewDraft(draft.id, 'approved')} style={{ flex: 1, padding: '6px', background: 'color-mix(in srgb, var(--st-success) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--st-success) 40%, transparent)', borderRadius: 6, color: 'var(--st-success)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✓ Approve</button>
                    <button onClick={() => reviewDraft(draft.id, 'rejected')} style={{ flex: 1, padding: '6px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: 'var(--st-error)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✗ Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {open && isMobile && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }}
        />
      )}

      {/* Dimming backdrop + click-out dismiss: MOBILE ONLY. On desktop an
          expanded panel is side-docked (the shell resizes beside it), so there
          is nothing to dim and the page must stay interactive — dismissing on
          click-out would make it impossible to work the page with chat open
          (Joe, 2026-08-07). Desktop closes via the panel's own ✕ / collapse. */}
      {open && mode === 'expanded' && !docked && isMobile && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 200 }}
        />
      )}

      {/* Mic used to live here as a separate sibling FAB; it's now folded
          into the Sage composer's action bar (see SageComposer). Nothing to
          render at this position anymore. */}

      {/* FAB — hidden while panel is open, hideFab is set, or the voice
          conversation card is currently in the FAB slot (mobile nav
          provides trigger in those cases). */}
      {!open && !hideFab && !conversationMode && (
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Open chat"
        onMouseEnter={() => setFabHovered(true)}
        onMouseLeave={() => setFabHovered(false)}
        style={{
          position: 'fixed',
          bottom: FAB_BOTTOM,
          right: FAB_RIGHT,
          zIndex: 202,
          width: FAB_SIZE,
          height: FAB_SIZE,
          borderRadius: '50%',
          background: 'var(--brand-gold)',
          border: '2px solid #36454F',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          transition: 'transform 0.15s, box-shadow 0.15s, background 0.2s',
          transform: fabHovered ? 'scale(1.08)' : 'scale(1)',
        }}
      >
        {open
          ? <X size={22} color="#1a1a1a" strokeWidth={2} />
          : <MessageSquare size={22} color="#0A0A0A" strokeWidth={2} />
        }
        {!open && unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: 6, right: 6,
            width: 10, height: 10, borderRadius: '50%',
            background: 'var(--st-error)', border: '2px solid var(--bg-page)',
          }} />
        )}
      </button>
      )}

      {/* Voice-mode overlay — takes over the viewport with a pulsing orb,
          live status, and transcript. Activated by the composer's Radio
          button via the sage:conversation-start event. */}
      <SageConversationMode
        open={conversationMode}
        onClose={() => setConversationMode(false)}
        fabBottom={FAB_BOTTOM}
        fabRight={FAB_RIGHT}
      />
      <AttachmentViewer attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
    </>
  )
}

export default ChatFABPanel
